import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setLocale, setLocaleISOCode, setUserStatus } from '../features/globalStates/globalStatesSlice';
import { useOktaAuth } from '@okta/okta-react';
import { IdxStatus } from '@okta/okta-auth-js';
import { getSAPUserByEmail, getUserByEmail, getUserByEmailForLogin } from '../utils/UserService/UserApis';
import { useIdxTransaction, useUserInfoContext } from '../contexts';
import { useModal } from '../hooks/useModal';
import { USER_STATUS } from '../constants';
import { getUserErrorMessages } from '../utils/getErrorMessage';
import { useFeatureFlag } from '../context/FeatureFlagContext';

export const useAuth = ({
  setInvalidPassword,
  setProcessing
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { oktaAuth } = useOktaAuth();
	const { setTransaction } = useIdxTransaction();
	const { setUserInfo } = useUserInfoContext() || '';
	const { handleCloseModal, handleLoginError } = useModal();
	const { globalErrorMessage } = useSelector(store => store?.globalMessages);
	
	const invalidCredentialsErrorData = globalErrorMessage && globalErrorMessage.length && globalErrorMessage.filter((data) => data?.fields?.code === "INVALID_CREDENTIALS");
	const invalidCredentialsError = invalidCredentialsErrorData && invalidCredentialsErrorData[0]?.fields?.message;

	const accountLockedErrorData = globalErrorMessage && globalErrorMessage.length && globalErrorMessage.filter((data) => data?.fields?.code === "ACCOUNT_LOCKED");
	const accountLockedError = accountLockedErrorData && accountLockedErrorData[0]?.fields?.message;
	const newLoginApiEnabled = useFeatureFlag(
		"IS_NEW_LOGIN_API_ENABLED"
	);

	const closeLoginModal = () => {
		handleCloseModal();
	};

	const signIn = useCallback(async (email, password, onSuccess, postLoginRedirectPath) => {
		manageLocalStorage.clearItems();
		try {
			setProcessing(true);
			let newTransaction = await oktaAuth.idx.authenticate();
			setTransaction(newTransaction);
			if (newTransaction.status === IdxStatus.SUCCESS) {
				setProcessing(false);
				oktaAuth.tokenManager.setTokens(newTransaction.tokens);
				await loadData(postLoginRedirectPath);
				onSuccess && onSuccess();
				closeLoginModal();
			} else {
				const newTransaction = await oktaAuth.idx.proceed({ username: email, password: password });
				const { status, tokens, messages } = newTransaction;
				if (status === IdxStatus.SUCCESS) {
					setProcessing(false);
					oktaAuth.tokenManager.setTokens(tokens);
					await loadData(postLoginRedirectPath);
					onSuccess && onSuccess();
					closeLoginModal();
				} else if (status === IdxStatus.PENDING) {
					if (localStorage.getItem('awaitingEmailVerification')) {
						history.push(`/registration/email-verification`);
						closeLoginModal();
					}
					setProcessing(false);
					sessionStorage.removeItem("okta-transaction-storage");
					if(messages){
						if (messages[0]?.i18n?.key === "errors.E0000004") {
							setInvalidPassword(`Invalid email or password. Please check your credentials and try again.`);
						}
						else if(messages[0]?.i18n?.key === "errors.E0000119"){
							setInvalidPassword(accountLockedError);
						}
						else {
							setInvalidPassword(messages[0].message);
						}
					}
					else if (newTransaction?.nextStep?.name === "reenroll-authenticator" &&
						newTransaction?.nextStep?.type === "password") {
						setInvalidPassword(`Your password has expired. Click 'Reset your password' to update it.`);
					}
					else if (!newTransaction.status.tokens){
						setTimeout(() => {
							window.location.href = newTransaction?.nextStep?.href;
						}, 100);
					}
					else{
						setInvalidPassword(`We've encountered an issue with your account, please refresh the page. If the problem persists, contact customer support.`);
					}
				} else if (status === IdxStatus.TERMINAL) {
					setProcessing(false);
					sessionStorage.removeItem("okta-transaction-storage");
					setInvalidPassword(messages[0].message);
				} else {
					setProcessing(false);
					sessionStorage.removeItem("okta-transaction-storage");
					setInvalidPassword(invalidCredentialsError);
				}
			}
		} catch (err) {
			setProcessing(false);
			setInvalidPassword("");
		}
	}, [dispatch, oktaAuth, setTransaction, setUserInfo, history, setInvalidPassword, setProcessing]);

	const fetchUserDetails = async (emailAddress) => {
		try {
		   const userByEmailResponse = newLoginApiEnabled
				? await dispatch(getUserByEmailForLogin({ emailAddress })).unwrap()
				: await dispatch(getUserByEmail({ emailAddress })).unwrap();
			await dispatch(getSAPUserByEmail({ emailAddress })).unwrap();
			return userByEmailResponse;
		} catch (error) { 
			handleLoginError(getUserErrorMessages(error?.data?.errorCode));
			throw error;
		}
	}

	const manageLocalStorage = {
		setItem: (key, value) => localStorage.setItem(key, value),
		clearItems: () => {
		  localStorage.clear();
		  sessionStorage.clear();
		},
		setInvalidUser: () => {
		  manageLocalStorage.clearItems();
		  localStorage.setItem("invalidUser", true);
		},
	};

	const processUserStatus = async (userStatus, history, redirectPath) => {
		switch (userStatus) {
			case USER_STATUS.UNVERIFIED:
				manageLocalStorage.setItem('awaitingEmailVerification', 'true');
				history.push(`/registration/email-verification`);
			break;
			case USER_STATUS.VERIFIED:
				manageLocalStorage.setItem('awaitingEmailVerification', 'false');
				history.push(`/onboarding/contact-info`);
			break;
			case USER_STATUS.ONBOARDING_PERSON_COMPLETE:
				history.push(`/onboarding/contact-info`);
			break;
			case USER_STATUS.ONBOARDING_ORG_COMPLETE:
				history.push(`/onboarding/shipping-address`);
			break;
			case USER_STATUS.ONBOARDING_SHIP_COMPLETE:
				history.push(`/onboarding/billing-address`);
			break;
			case USER_STATUS.ONBOARDING_BILL_COMPLETE:
				history.push(`/onboarding/medical-identification`);
			break;
			case USER_STATUS.ACTIVE:
				history.push(redirectPath || `/home`);
			break;
			default:
				manageLocalStorage.setInvalidUser();
				await oktaAuth.signOut();
		}
	};

	const updateUserConfiguration = (user) => {
		localStorage.setItem("currentUser", JSON.stringify(user));
		setUserInfo(user);
		const isocode = user.country?.isocode || 'US';
		const locale = isocode === "DE" ? 'en-DE' : 'en-US';
		dispatch(setLocale(locale));
		dispatch(setLocaleISOCode(isocode));
	}
	
	const loadData = useCallback(async (postLoginRedirectPath) => {
		setProcessing(true);
		try {
			const userDetails = await oktaAuth.getUser();
			let user = null;
			user = await fetchUserDetails(userDetails?.email);

			let userStatus = user?.status || user?.userStatus;

			dispatch(setUserStatus(userStatus));
			await processUserStatus(userStatus, history, postLoginRedirectPath);
			updateUserConfiguration(user);
		} catch (error) {
			if (error?.data?.message) {
				throw error.data.message;
			} else {
				manageLocalStorage.setInvalidUser();
				await oktaAuth.signOut();
				setInvalidPassword(error);
			}
		} finally {
			setProcessing(false);
		}
  	}, [dispatch, oktaAuth, setUserInfo, getUserByEmail, getUserByEmailForLogin]);
  	
	return { signIn, loadData };
};
